// components/Footer.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Link,
  IconButton,
  Divider,
  Stack,
  Dialog,
  DialogContent,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import EmailIcon from '@mui/icons-material/Email';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import InstagramIcon from '@mui/icons-material/Instagram';
import VerifiedIcon from '@mui/icons-material/Verified';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CloseIcon from '@mui/icons-material/Close';
import LanguageIcon from '@mui/icons-material/Language';

import Logo from '../logo.png';

const Footer = () => {
  const [open, setOpen] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePos({
        x: (e.clientX / window.innerWidth - 0.5) * 40,
        y: (e.clientY / window.innerHeight - 0.5) * 40,
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const quickLinks = [
    { label: 'Home', path: '/' },
    { label: 'About', path: '/about' },
    { label: 'Products', path: '/products' },
    { label: 'Clients', path: '/clients' },
    { label: 'Careers', path: '/careers' },
    { label: 'Contact', path: '/contact' },
  ];

  const legalLinks = [
    { label: 'Privacy Policy', path: '/privacy-policy' },
    { label: 'Terms', path: '/terms' },
    { label: 'Disclaimer', path: '/disclaimer' },
    { label: 'Cookie Policy', path: '/cookie-policy' },
  ];

  return (
    <>
      <Box
        component="footer"
        sx={{
          position: 'relative',
          overflow: 'hidden',
          background: 'radial-gradient(ellipse at top, #0a0a0f 0%, #070710 40%, #050508 100%)',
          color: '#E5E7EB',
          pt: 7,
          pb: 4,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '1px',
            background: 'linear-gradient(90deg, transparent, rgba(124, 58, 237, 0.4), transparent)',
          },
        }}
      >
        {/* Animated geometric background */}
        <Box className="geometric-bg">
          {/* Large floating triangles */}
          {[...Array(3)].map((_, i) => (
            <div
              key={`tri-${i}`}
              className="floating-triangle"
              style={{
                '--delay': `${i * 2}s`,
                '--duration': `${20 + i * 5}s`,
                transform: `translate(${mousePos.x * 0.3}px, ${mousePos.y * 0.3}px)`,
              }}
            />
          ))}

          {/* Rotating hexagons */}
          {[...Array(4)].map((_, i) => (
            <div
              key={`hex-${i}`}
              className="rotating-hexagon"
              style={{
                '--size': `${60 + i * 20}px`,
                '--delay': `${i * 0.5}s`,
                top: `${10 + i * 20}%`,
                left: `${i * 25}%`,
              }}
            />
          ))}

          {/* Grid dots pattern */}
          <div className="grid-dots" />

          {/* Floating cubes */}
          {[...Array(5)].map((_, i) => (
            <div
              key={`cube-${i}`}
              className="floating-cube"
              style={{
                '--delay': `${i * 1.5}s`,
                '--duration': `${25 + i * 3}s`,
                right: `${10 + i * 15}%`,
                bottom: `${5 + i * 10}%`,
              }}
            />
          ))}

          {/* Subtle particles */}
          {[...Array(20)].map((_, i) => (
            <div
              key={`particle-${i}`}
              className="geometric-particle"
              style={{
                '--delay': `${i * 0.3}s`,
                '--duration': `${8 + Math.random() * 10}s`,
                width: `${2 + Math.random() * 4}px`,
                height: `${2 + Math.random() * 4}px`,
              }}
            />
          ))}
        </Box>

        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
          {/* MAIN GRID */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1.5fr 1fr' },
              gap: 5,
            }}
          >
            {/* BRAND */}
            <Box>
              <Stack direction="row" spacing={2} alignItems="center" mb={1}>
                <Box
                  component="img"
                  src={Logo}
                  sx={{
                    height: 46,
                    filter: 'drop-shadow(0 4px 12px rgba(124, 58, 237, 0.3))',
                  }}
                />
                <Typography
                  variant="h6"
                  fontWeight={700}
                  sx={{
                    background: 'linear-gradient(135deg, #E5E7EB 0%, #9CA3AF 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  Simiga Consummate
                </Typography>
                <VerifiedIcon sx={{ color: '#22D3EE' }} />
              </Stack>

              <Typography
                variant="body2"
                sx={{
                  opacity: 0.8,
                  maxWidth: 380,
                  lineHeight: 1.7,
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: '-16px',
                    top: 0,
                    bottom: 0,
                    width: '2px',
                    background: 'linear-gradient(to bottom, transparent, #7C3AED, transparent)',
                  },
                }}
              >
                Simiga Consummate & Minerals Limited delivers quality agro-commodities with trust,
                precision and long-term partnerships across India.
              </Typography>

              {/* Social */}
              <Stack direction="row" spacing={1.5} mt={3}>
                {[FacebookIcon, TwitterIcon, LinkedInIcon, InstagramIcon].map((Icon, i) => (
                  <IconButton key={i} className="socialGlow">
                    <Icon />
                  </IconButton>
                ))}
              </Stack>

              {/* Address */}
              <Stack direction="row" spacing={2} mt={3} alignItems="flex-start">
                <Box className="iconBox locationGlow">
                  <LocationOnIcon />
                </Box>
                <Typography variant="body2" sx={{ lineHeight: 1.7, opacity: 0.85 }}>
                  Premises No. 31-0166, Plot No. BD/60,<br />
                  Action Area-1, Newtown,<br />
                  Kolkata – 700156, West Bengal, India
                </Typography>
              </Stack>
            </Box>

            {/* COMPANY LINKS */}
            <Box>
              <Typography
                fontWeight={700}
                mb={2}
                sx={{
                  display: 'inline-block',
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: '-4px',
                    left: 0,
                    width: '40px',
                    height: '2px',
                    background: 'linear-gradient(90deg, #7C3AED, transparent)',
                  },
                }}
              >
                Company
              </Typography>
              {quickLinks.map((link) => (
                <Link
                  key={link.label}
                  component={RouterLink}
                  to={link.path}
                  underline="none"
                  className="footerLink"
                >
                  {link.label}
                </Link>
              ))}
            </Box>

            {/* CONTACT */}
            <Box>
              <Typography
                fontWeight={700}
                mb={2}
                sx={{
                  display: 'inline-block',
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: '-4px',
                    left: 0,
                    width: '40px',
                    height: '2px',
                    background: 'linear-gradient(90deg, #38BDF8, transparent)',
                  },
                }}
              >
                Contact
              </Typography>
              <Stack spacing={2.2}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box className="iconBox mobileGlow"><PhoneAndroidIcon /></Box>
                  <Typography variant="body2">+91 74494 33633</Typography>
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box className="iconBox telGlow"><LocalPhoneIcon /></Box>
                  <Typography variant="body2">033-4525-5063</Typography>
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box className="iconBox mailGlow"><EmailIcon /></Box>
                  <Typography variant="body2">simigaconsummate@gmail.com</Typography>
                </Stack>
              </Stack>
            </Box>

            {/* TRUST */}
            <Box>
              <Typography
                fontWeight={700}
                mb={2}
                sx={{
                  display: 'inline-block',
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: '-4px',
                    left: 0,
                    width: '40px',
                    height: '2px',
                    background: 'linear-gradient(90deg, #22C55E, transparent)',
                  },
                }}
              >
                Trust & Compliance
              </Typography>
              <Stack spacing={1.1} sx={{ opacity: 0.85 }}>
                <Typography variant="body2">ISO Certified</Typography>
                <Typography variant="body2">GST Registered</Typography>
                <Typography variant="body2">Corporate Verified</Typography>
                <Typography variant="body2">Trusted Partner Network</Typography>
              </Stack>
            </Box>
          </Box>

          {/* LEGAL LINKS BAR */}
          <Box
            sx={{
              mt: 5,
              pt: 3,
              borderTop: '1px solid rgba(255,255,255,0.08)',
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: 2,
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: '-1px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '100px',
                height: '1px',
                background: 'linear-gradient(90deg, transparent, #7C3AED, transparent)',
              },
            }}
          >
            {legalLinks.map((item, i) => (
              <Link
                key={i}
                component={RouterLink}
                to={item.path}
                underline="none"
                sx={{
                  color: '#9CA3AF',
                  fontSize: 13,
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    bottom: '-2px',
                    left: 0,
                    width: '0%',
                    height: '1px',
                    background: 'linear-gradient(90deg, #7C3AED, #38BDF8)',
                    transition: 'width 0.3s ease',
                  },
                  '&:hover': {
                    color: 'white',
                    '&::before': {
                      width: '100%',
                    },
                  },
                }}
              >
                {item.label}
              </Link>
            ))}
          </Box>

          <Divider sx={{ my: 4, borderColor: 'rgba(255,255,255,0.08)' }} />

          {/* BOTTOM BAR */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <Typography variant="caption" sx={{ opacity: 0.6 }}>
              © {new Date().getFullYear()} Simiga Consummate & Minerals Limited. All rights reserved.
            </Typography>

            <Typography
              variant="caption"
              className="developerLink"
              onClick={() => setOpen(true)}
              sx={{
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: '-100%',
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(90deg, transparent, rgba(124, 58, 237, 0.2), transparent)',
                  transition: 'left 0.7s ease',
                },
                '&:hover::before': {
                  left: '100%',
                },
              }}
            >
              Developed by AediaX Tech Private Limited
            </Typography>
          </Box>
        </Container>

        {/* ===== PREMIUM GEOMETRIC STYLES ===== */}
        <style>{`
          .geometric-bg {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            z-index: 1;
            overflow: hidden;
          }

          /* Floating triangles */
          .floating-triangle {
            position: absolute;
            width: 0;
            height: 0;
            border-left: 80px solid transparent;
            border-right: 80px solid transparent;
            border-bottom: 140px solid rgba(124, 58, 237, 0.05);
            animation: float-triangle var(--duration, 20s) var(--delay, 0s) infinite linear;
            filter: blur(1px);
          }

          @keyframes float-triangle {
            0%, 100% {
              transform: translate(0, 0) rotate(0deg);
              opacity: 0.05;
            }
            25% {
              transform: translate(100px, -50px) rotate(90deg);
              opacity: 0.1;
            }
            50% {
              transform: translate(50px, 100px) rotate(180deg);
              opacity: 0.05;
            }
            75% {
              transform: translate(-100px, 50px) rotate(270deg);
              opacity: 0.1;
            }
          }

          /* Rotating hexagons */
          .rotating-hexagon {
            position: absolute;
            width: var(--size, 60px);
            height: var(--size, 60px);
            background: linear-gradient(45deg, rgba(56, 189, 248, 0.03), rgba(124, 58, 237, 0.03));
            clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
            animation: rotate-hexagon 20s var(--delay, 0s) infinite linear;
          }

          @keyframes rotate-hexagon {
            0% {
              transform: rotate(0deg) scale(1);
              opacity: 0.03;
            }
            50% {
              transform: rotate(180deg) scale(1.1);
              opacity: 0.06;
            }
            100% {
              transform: rotate(360deg) scale(1);
              opacity: 0.03;
            }
          }

          /* Grid dots */
          .grid-dots {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background-image: radial-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px);
            background-size: 40px 40px;
            mask-image: radial-gradient(circle at center, black, transparent 70%);
          }

          /* Floating cubes */
          .floating-cube {
            position: absolute;
            width: 40px;
            height: 40px;
            background: linear-gradient(45deg, rgba(34, 211, 238, 0.03), rgba(124, 58, 237, 0.03));
            transform: rotate(45deg);
            animation: float-cube var(--duration, 25s) var(--delay, 0s) infinite ease-in-out;
          }

          @keyframes float-cube {
            0%, 100% {
              transform: translateY(0) rotate(45deg) scale(1);
              opacity: 0.03;
            }
            50% {
              transform: translateY(-100px) rotate(225deg) scale(1.1);
              opacity: 0.06;
            }
          }

          /* Geometric particles */
          .geometric-particle {
            position: absolute;
            background: rgba(255, 255, 255, 0.1);
            border-radius: 50%;
            animation: particle-move var(--duration, 10s) var(--delay, 0s) infinite linear;
          }

          @keyframes particle-move {
            0% {
              transform: translate(0, 0);
              opacity: 0;
            }
            10% {
              opacity: 0.3;
            }
            90% {
              opacity: 0.3;
            }
            100% {
              transform: translate(calc(100vw * 1.5), calc(-100vh * 1.5));
              opacity: 0;
            }
          }

          /* Keep existing styles with minor enhancements */
          .socialGlow {
            color: #9CA3AF;
            border: 1px solid rgba(255,255,255,0.1);
            transition: all 0.35s ease;
            backdrop-filter: blur(10px);
            background: rgba(255, 255, 255, 0.02);
          }

          .socialGlow:hover {
            color: white;
            background: linear-gradient(135deg, rgba(37, 99, 235, 0.9), rgba(124, 58, 237, 0.9));
            box-shadow: 0 0 30px rgba(124, 58, 237, 0.6);
            transform: translateY(-4px) scale(1.1);
          }

          .iconBox {
            width: 48px;
            height: 48px;
            border-radius: 14px;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
            transition: transform 0.3s ease;
          }

          .iconBox:hover {
            transform: translateY(-2px);
          }

          .mobileGlow {
            background: linear-gradient(135deg, rgba(34, 197, 94, 0.9), rgba(22, 163, 74, 0.9));
            box-shadow: 0 4px 20px rgba(34, 197, 94, 0.3);
          }

          .telGlow {
            background: linear-gradient(135deg, rgba(56, 189, 248, 0.9), rgba(37, 99, 235, 0.9));
            box-shadow: 0 4px 20px rgba(56, 189, 248, 0.3);
          }

          .mailGlow {
            background: linear-gradient(135deg, rgba(167, 139, 250, 0.9), rgba(124, 58, 237, 0.9));
            box-shadow: 0 4px 20px rgba(124, 58, 237, 0.3);
          }

          .locationGlow {
            background: linear-gradient(135deg, rgba(245, 158, 11, 0.9), rgba(217, 119, 6, 0.9));
            box-shadow: 0 4px 20px rgba(245, 158, 11, 0.3);
          }

          .footerLink {
            display: block;
            color: #9CA3AF;
            margin-bottom: 12px;
            font-size: 14px;
            transition: all 0.3s ease;
            padding: 4px 0;
            position: relative;
            overflow: hidden;
          }

          .footerLink:hover {
            color: white;
            padding-left: 8px;
            transform: translateX(4px);
          }

          .developerLink {
            opacity: 0.85;
            cursor: pointer;
            color: #A78BFA;
            transition: all 0.3s ease;
            font-weight: 500;
          }

          .developerLink:hover {
            color: white;
            text-decoration: underline;
            transform: translateY(-2px);
            text-shadow: 0 2px 10px rgba(167, 139, 250, 0.5);
          }
        `}</style>
      </Box>

      {/* AEDIAX DIALOG */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            background: 'transparent',
            boxShadow: 'none',
          },
        }}
      >
        <DialogContent
          sx={{
            background: 'linear-gradient(135deg, rgba(10, 10, 15, 0.95), rgba(7, 7, 16, 0.95))',
            backdropFilter: 'blur(30px)',
            color: 'white',
            textAlign: 'center',
            p: 5,
            minWidth: 350,
            border: '1px solid rgba(124, 58, 237, 0.2)',
            borderRadius: '20px',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '1px',
              background: 'linear-gradient(90deg, transparent, #7C3AED, transparent)',
            },
          }}
        >
          <IconButton
            onClick={() => setOpen(false)}
            sx={{
              position: 'absolute',
              right: 12,
              top: 12,
              color: 'white',
              background: 'rgba(255, 255, 255, 0.1)',
              '&:hover': {
                background: 'rgba(124, 58, 237, 0.3)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>

          <Box
            component="img"
            src="https://aediax.com/static/media/logo.fb254eb9a5d0c80f4f86.png"
            sx={{
              height: 80,
              mb: 3,
              filter: 'drop-shadow(0 4px 20px rgba(124, 58, 237, 0.4))',
            }}
          />

          <Typography
            variant="h6"
            fontWeight={700}
            sx={{
              background: 'linear-gradient(135deg, #E5E7EB 0%, #9CA3AF 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            AediaX Tech Private Limited
          </Typography>

          <Typography variant="body2" sx={{ mt: 1, opacity: 0.85 }}>
            info@aediax.com
          </Typography>

          <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.85 }}>
            +91-78549-98757
          </Typography>

          <Link
            href="https://www.aediax.com"
            target="_blank"
            underline="none"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              mt: 3,
              px: 3,
              py: 1.5,
              color: '#A78BFA',
              border: '1px solid rgba(167, 139, 250, 0.3)',
              borderRadius: '10px',
              transition: 'all 0.3s ease',
              '&:hover': {
                color: 'white',
                background: 'rgba(124, 58, 237, 0.2)',
                borderColor: '#7C3AED',
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 20px rgba(124, 58, 237, 0.3)',
              },
            }}
          >
            <LanguageIcon />
            www.aediax.com
          </Link>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Footer;